import React, { useState, useMemo } from 'react';
import { usePostsStore } from '../stores';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar as CalendarIcon,
  Clock,
  MoreHorizontal,
  Edit,
  Trash2,
  Send,
  Twitter,
  Instagram,
  Linkedin,
  Facebook,
  Youtube,
  Music,
  Pin,
  AtSign,
} from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday,
  parseISO,
} from 'date-fns';
import { Link } from 'react-router-dom';

const platformIcons = {
  twitter: Twitter,
  instagram: Instagram,
  linkedin: Linkedin,
  facebook: Facebook,
  youtube: Youtube,
  tiktok: Music,
  pinterest: Pin,
  threads: AtSign,
};

const statusColors = {
  scheduled: 'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400',
  published: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

export default function Calendar() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [view, setView] = useState('month'); // 'month', 'week', 'list'
  
  const { posts, deletePost, publishNow } = usePostsStore();
  
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  
  const calendarDays = useMemo(() => {
    const days = [];
    let day = calendarStart;
    
    while (day <= calendarEnd) {
      days.push(day);
      day = addDays(day, 1);
    }
    
    return days;
  }, [calendarStart, calendarEnd]);
  
  const getPostsForDate = (date) => {
    return posts.filter(post => {
      const postDate = post.scheduledAt ? parseISO(post.scheduledAt) : null;
      return postDate && isSameDay(postDate, date);
    });
  };
  
  const selectedDatePosts = selectedDate ? getPostsForDate(selectedDate) : [];
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Content Calendar</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {posts.filter(p => p.status === 'scheduled').length} posts scheduled
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* View toggle */}
          <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            {['month', 'week', 'list'].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md capitalize transition-colors ${
                  view === v
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {v}
              </button>
            ))}
          </div>
          
          <Link to="/create" className="btn-primary">
            <Plus className="w-4 h-4 mr-1.5" />
            New Post
          </Link>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-3 card">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => setCurrentMonth(new Date())}
                className="px-3 py-1.5 text-sm font-medium rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                Today
              </button>
              <button
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Calendar grid */}
          <div className="p-4">
            {/* Day headers */}
            <div className="grid grid-cols-7 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Days */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, i) => {
                const dayPosts = getPostsForDate(day);
                const isCurrentMonth = isSameMonth(day, currentMonth);
                const isSelected = selectedDate && isSameDay(day, selectedDate);
                
                return (
                  <button
                    key={i}
                    onClick={() => setSelectedDate(day)}
                    className={`
                      min-h-24 p-2 rounded-lg text-left transition-colors
                      ${isCurrentMonth ? '' : 'opacity-40'}
                      ${isSelected 
                        ? 'bg-brand-50 dark:bg-brand-900/20 ring-2 ring-brand-500' 
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                      }
                      ${isToday(day) ? 'bg-brand-50/50 dark:bg-brand-900/10' : ''}
                    `}
                  >
                    <span className={`
                      inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-medium
                      ${isToday(day) ? 'bg-brand-500 text-white' : 'text-gray-700 dark:text-gray-300'}
                    `}>
                      {format(day, 'd')}
                    </span>
                    
                    {/* Post indicators */}
                    <div className="mt-1 space-y-1">
                      {dayPosts.slice(0, 3).map((post) => (
                        <div
                          key={post.id}
                          className={`text-xs px-1.5 py-0.5 rounded truncate ${statusColors[post.status]}`}
                        >
                          {format(parseISO(post.scheduledAt), 'h:mm a')}
                        </div>
                      ))}
                      {dayPosts.length > 3 && (
                        <div className="text-xs text-gray-500 px-1.5">
                          +{dayPosts.length - 3} more
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Selected day details */}
        <div className="card">
          <div className="p-4 border-b border-gray-200 dark:border-gray-800">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {selectedDate ? format(selectedDate, 'EEEE, MMM d') : 'Select a date'}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {selectedDatePosts.length} post{selectedDatePosts.length !== 1 ? 's' : ''} scheduled
            </p>
          </div>
          
          <div className="p-4 space-y-3">
            {selectedDate ? (
              selectedDatePosts.length > 0 ? (
                selectedDatePosts.map((post) => (
                  <div key={post.id} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 dark:text-white line-clamp-2">
                          {post.content}
                        </p>
                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            {post.platforms.map(p => {
                              const Icon = platformIcons[p] || CalendarIcon;
                              return <Icon key={p} className="w-3.5 h-3.5 text-gray-400" />;
                            })}
                          </div>
                          <span className="text-xs text-gray-500">
                            {format(parseISO(post.scheduledAt), 'h:mm a')}
                          </span>
                          <span className={`text-xs px-1.5 py-0.5 rounded capitalize ${statusColors[post.status]}`}>
                            {post.status}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <button className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => deletePost(post.id)}
                          className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <CalendarIcon className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No posts scheduled</p>
                  <Link 
                    to="/create" 
                    className="text-brand-500 hover:text-brand-600 text-sm font-medium mt-1 inline-block"
                  >
                    Create a post
                  </Link>
                </div>
              )
            ) : (
              <div className="text-center py-8">
                <CalendarIcon className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Click a date to view posts</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
